import request from '@/utils/request';
import { errorMsg, warningMsg } from '@/utils/message';
import { errorNotify } from '@/utils/errorNotification';
import { ElNotification, ElMessage } from 'element-plus';
import { gwDevStatus, topicType, defaultAreaWords, serviceId } from '@/utils/const';
import { publishTopic, getTopic } from '@/utils/mqtt';
import { i18n } from '@/Lang/index';
import { getGWIdList } from '@/api/gateway';
import { gatewayPermissionRequest } from '@/utils/serviceContext';

/**
 * @typedef {object} Result
 * @property {number} status - 実行結果のステータス
 * @property {any}data - 実行結果のデータ
 */

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.name - エリアの名前
 * @return {Promise<Result>} 実行結果のオブジェクト（adminのみ）
 */
export async function createArea(data) {
  let res = { status: 0 };
  try {
    await request({
      url: '/areamasters/rwm/methods/area',
      method: 'post',
      data: {
        name: data.name,
        status: '1',
      },
    });
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @return {Promise<Result>} 実行結果のオブジェクト（data.areaList: エリア一覧）
 */
export async function getAreaList() {
  let res_areaList = [];
  try {
    const res = await request({
      url: '/areamasters/rwm/methods/arealist',
      method: 'get',
    });
    res_areaList = res;
  } catch {
    // TODO
    const res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  const res_data = {
    count: res_areaList.length,
    areaList: res_areaList,
  };
  return { status: 0, data: res_data };
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - エリアのID
 * @return {Promise<Result>} 実行結果のオブジェクト（adminのみ）
 */
export async function deleteArea(data) {
  let res = { status: 0 };
  try {
    const gwList = await getGWIdList({ area: data.area, service: serviceId.basic });
    await request({
      url: '/areamasters/rwm/methods/area',
      method: 'delete',
      params: {
        area_id: data.area,
      },
    });
    const payload = {
      area: data.area,
    };
    const mqttData = {
      target: data.tenant,
      payload: payload,
    };
    const mqttTopic = getTopic(topicType.areaDeleted, {
      tenant: data.tenant,
      target: data.tenant,
    });
    publishTopic(mqttTopic, mqttData);

    // Update Dispalyed Old Data
    await setAreaGWStatusDeactivated({
      tenant: data.tenant,
      area: data.area,
      gwList: gwList,
    });
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.area - エリアのID
 * @param {string} data.name - エリアの名前
 * @return {Promise<Result>} 実行結果のオブジェクト（adminのみ）
 */
export async function editAreaName(data) {
  let res = { status: 0 };
  try {
    await request({
      url: '/areamasters/rwm/methods/area',
      method: 'post',
      params: {
        area_id: data.area,
      },
      data: {
        name: data.name,
        status: '1', // TODO: upadate only name
      },
    });
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.name - エリアの名前
 * @return {Promise<Boolean>} エリアの名前の重複チェックの結果の真偽値
 */
export async function areaNameDuplicateCheck(data) {
  let areaNameDuplicated = false;
  const areaNameDuplicateNotify = () => {
    ElMessage({
      message: warningMsg.Duplicated_Area_Name,
      type: 'warning',
      duration: 5 * 1000,
      customClass: 'notify',
    });
  };
  try {
    if (!defaultAreaWords.includes(data.name)) {
      const res = await getAreaList();
      const areaList = res.data.areaList;
      if (areaList.length > 0) {
        const duplicatedArea = areaList.find((area) => area.name === data.name);
        if (duplicatedArea && data.name !== '') {
          areaNameDuplicateNotify();
          areaNameDuplicated = true;
        }
      }
    } else {
      areaNameDuplicateNotify();
      areaNameDuplicated = true;
    }
  } catch {
    areaNameDuplicated = true;
  }
  return areaNameDuplicated;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - 移動先のエリアのID
 * @param {string[]} data.gwList - ゲートウェイのIDのリスト
 * @return {Promise<Result>} 実行結果のオブジェクト（adminのみ）
 */
export async function registerGWs(data) {
  let res = { status: 0 };
  try {
    const tmpAreas = data.gwList.map((gw) => gw.areaId);
    const allAreas = Array.from(new Set(tmpAreas));
    for (const area of allAreas) {
      const targetGWList = data.gwList.filter((gw) => gw.areaId === area);
      const resData = await changeAreaGW({
        tenant: data.tenant,
        area: data.area,
        oldArea: area,
        gwList: targetGWList,
      });
      if (resData.status !== 0) {
        res = resData;
      }
    }
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - 移動先のエリアのID
 * @param {string} data.oldArea - 移動元のエリアのID
 * @param {string[]} data.gwList - ゲートウェイのIDのリスト
 * @return {Promise<Result>} 実行結果のオブジェクト（adminのみ）
 */
export async function changeAreaGW(data) {
  let res = { status: 0 };
  const deactivatedGWList = [];
  const registerGWFailedNotify = (gw) => {
    ElNotification({
      title: i18n.global.t('area.error'),
      message: errorMsg.e_register_gw_failed + `: ${gw.devId}`,
      type: 'error',
      duration: 2000,
      customClass: 'notify',
    });
  };

  try {
    await Promise.allSettled(
      data.gwList.map(async (gw) => {
        try {
          await gatewayPermissionRequest({
            url: '/areamasters/rwm/methods/areachange',
            method: 'post',
            params: {
              area_id: data.area,
              old_area_id: data.oldArea,
              gateway_id: gw.devId,
            },
          });
          deactivatedGWList.push(gw);
        } catch {
          registerGWFailedNotify(gw);
          res = { status: -2 };
        }
      })
    );

    // Update Dispalyed Old Data
    await setAreaGWStatusDeactivated({
      tenant: data.tenant,
      area: data.oldArea,
      gwList: deactivatedGWList,
    });
  } catch {
    // TODO
    res = {
      status: -2,
      data: [{ cause: errorMsg.e_request_failed_err }],
    };
    errorNotify(res);
  }
  return res;
}

/**
 * @param {Object} data 入力データオブジェクト
 * @param {string} data.tenant - テナントのID
 * @param {string} data.area - エリアのID
 * @param {string[]} data.gwList - ゲートウェイのリスト
 * @return {Promise<void>}
 */
async function setAreaGWStatusDeactivated(data) {
  const timestamp = Date.now();
  const status = gwDevStatus.deactivated;
  for (const gw of data.gwList) {
    const payload = {
      gateway: gw.devId,
      timestamp: timestamp,
      status: status,
    };
    const mqttData = {
      target: data.area,
      payload: payload,
    };
    const mqttTopic = getTopic(topicType.gatewayStatusChange, {
      tenant: data.tenant,
      target: data.area,
    });
    await publishTopic(mqttTopic, mqttData);
  }
}
